// ============================================================================
// TIMESHEET STATUS REPORT
// ============================================================================
// Two view modes:
//   - List  → one row per (employee, week) with status / logged / min / shortfall.
//   - Pivot → one row per employee, one column per week. Each cell stacks
//             Logged / Min / Shortfall on three lines (Shortfall hidden when 0).
//             Cap: at most 8 weeks (auto-clamp + banner if user picked wider).
//
// Pivot keeps the same backend (/Reports/timesheet-status); the screen pulls
// long rows in one large page and pivots client-side via useMemo.

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect, SyutScopedEmployeeSelect } from 'components/ui';
import { useTimesheetStatusReport } from 'hooks/api/useReport';
import { useEmployees } from 'hooks/api/useEmployee';
import { useAuthContext } from 'contexts/AuthContext';
import type { TimesheetStatusReportRequest, TimesheetStatusReportRow } from 'api/types/report.types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatMinutesToHours(minutes: number): string {
  if (!minutes || minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '—';
  const parts = dateStr.split('T')[0].split('-').map(Number);
  if (parts.length < 3) return '—';
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

/** Compact column header label for a week — "Week of 16-Apr". */
function formatWeekHeader(dateStr: string): string {
  const parts = dateStr.split('T')[0].split('-').map(Number);
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]}`;
}

function firstOfMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'NOT_STARTED', label: 'Not Started' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SUBMITTED', label: 'Submitted' },
];

// Pivot mode hard cap. Wider date ranges are auto-clamped to the most recent
// 8 weeks with a banner — keeps the table from becoming unreadable.
const PIVOT_MAX_WEEKS = 8;
// Defense-in-depth: even with the date-range clamp below, page size needs to
// cover (PIVOT_MAX_WEEKS × max-reportees-in-scope) long rows. The backend's
// SQL emits one row per (employee × week) — 8 weeks × 625 reportees = 5000.
const PIVOT_PAGE_SIZE = 5000;

type ViewMode = 'list' | 'pivot';

// ---------------------------------------------------------------------------
// Pivot row shape
// ---------------------------------------------------------------------------

interface PivotCell {
  statusCode: string;
  loggedMinutes: number;
  minimumMinutes: number;
  shortfallMinutes: number;
}

interface PivotRow {
  employeeId: string;
  employeeCode?: string | null;
  employeeName?: string | null;
  weeks: Record<string, PivotCell>;
  totalLogged: number;
  totalMinimum: number;
  totalShortfall: number;
}

interface TimesheetStatusReportScreenProps {
  /** Module-action codes granted to this user for the timesheet-status-report module. */
  allowedActions?: string[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TimesheetStatusReportScreen: React.FC<TimesheetStatusReportScreenProps> = ({ allowedActions = [] }) => {
  const { user } = useAuthContext();

  // Permission gating drives the DEFAULT manager filter only. The Manager
  // dropdown's options are loaded server-side via /Employees/visible.
  const canViewAll = allowedActions.includes('view-all-employees');

  // ----- View mode (list / pivot) — persisted in URL -----
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'list';
    const params = new URLSearchParams(window.location.search);
    return params.get('view') === 'pivot' ? 'pivot' : 'list';
  });

  // Mirror viewMode into the URL so a bookmarked / shared link round-trips.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (viewMode === 'pivot') url.searchParams.set('view', 'pivot');
    else url.searchParams.delete('view');
    // replaceState — no history entry per toggle; user expects Back to leave the screen.
    window.history.replaceState({}, '', url.toString());
  }, [viewMode]);

  const isPivot = viewMode === 'pivot';

  // ----- Query / filter state -----
  const [queryParams, setQueryParams] = useState<TimesheetStatusReportRequest>({
    page: 1,
    pageSize: 25,
    fromDate: firstOfMonth(),
    toDate: today(),
    // Initial allEmployees default — flipped off below for non-admin callers
    // once their employee row resolves (see effect).
    allEmployees: true,
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter working state
  const [fromDate, setFromDate] = useState(queryParams.fromDate || '');
  const [toDate, setToDate] = useState(queryParams.toDate || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('');

  // Pivot-mode sort: by employee name (default) or by total shortfall desc.
  const [pivotSort, setPivotSort] = useState<'name' | 'shortfall'>('name');

  // Resolve logged-in user → employee row, used as the default Manager anchor
  // when canViewAll is false. Without this default the report comes back blank
  // because the backend has no anchor to scope against.
  const { data: selfEmployees } = useEmployees({ pageSize: 1, userId: user?.userId });
  const currentEmployee = useMemo(() => selfEmployees[0] || null, [selfEmployees]);

  // Default the Manager filter to the logged-in employee when the caller
  // cannot view all. Pushes the same value into queryParams so the very
  // first fetch is scoped — no blank-then-populate flash.
  useEffect(() => {
    if (managerFilter) return;
    if (canViewAll) return;
    if (!currentEmployee?.id) return;
    setManagerFilter(currentEmployee.id);
    setQueryParams(prev => ({
      ...prev,
      managerId: currentEmployee.id,
      allEmployees: false,
      page: 1,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmployee?.id, canViewAll]);

  // Seed option keeps the auto-selected self id visible while the dropdown
  // loads its async options.
  const seedSelfOption = useMemo(() => {
    if (!currentEmployee) return undefined;
    return [{
      value: currentEmployee.id,
      label: `${currentEmployee.name || ''}${currentEmployee.code ? ` (${currentEmployee.code})` : ''}`.trim(),
    }];
  }, [currentEmployee]);

  // Search debounce
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ----- Pivot mode data shaping -----
  // In pivot mode we adjust the API call:
  //   1. Single large page (no paging UI).
  //   2. Status filter suppressed (its semantics — "show only Draft" — don't
  //      translate to a per-employee row).
  //   3. Date range narrowed server-side to the most recent PIVOT_MAX_WEEKS
  //      weeks of the user's selected range. The backend emits one long row
  //      per (employee × week-in-range); a 56-week range × 150 reportees would
  //      be 8400 rows and the LIMIT would silently truncate earlier weeks
  //      (which is exactly the "only 7 weeks shown" bug). Clamping the range
  //      keeps the row count bounded at 8 × N.
  // The original `queryParams` is left untouched so toggling back to List mode
  // restores the user's full selection.
  const effectiveQuery = useMemo<TimesheetStatusReportRequest>(() => {
    if (!isPivot) return queryParams;

    const userFrom = queryParams.fromDate || firstOfMonth();
    const userTo = queryParams.toDate || today();
    // Re-derive a "narrow" fromDate that anchors the window at the most-recent
    // PIVOT_MAX_WEEKS × 7 days before the user's toDate. Spans on the calendar
    // straddle week boundaries, so we add one extra week of cushion to make
    // sure we still surface 8 distinct Wednesday-week-starts after the
    // backend's week-series CTE snaps to its canonical week-start day.
    const toMs = new Date(userTo).getTime();
    const fromMsRequested = new Date(userFrom).getTime();
    const cushionDays = (PIVOT_MAX_WEEKS + 1) * 7;
    const narrowFromMs = Math.max(fromMsRequested, toMs - cushionDays * 86400000);
    const d = new Date(narrowFromMs);
    const narrowFromDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    return {
      ...queryParams,
      page: 1,
      pageSize: PIVOT_PAGE_SIZE,
      statusFilter: undefined,
      fromDate: narrowFromDate,
    };
  }, [isPivot, queryParams]);

  const { data: rawData, pagination, loading, refetch } = useTimesheetStatusReport(effectiveQuery);

  // True when the user's selected (full) date range exceeds the pivot cap.
  // Drives the cap banner so the message reflects the user's intent, not the
  // narrowed API window.
  const userRangeExceedsCap = useMemo(() => {
    if (!isPivot) return false;
    const f = queryParams.fromDate ? new Date(queryParams.fromDate).getTime() : 0;
    const t = queryParams.toDate ? new Date(queryParams.toDate).getTime() : 0;
    if (!f || !t || t <= f) return false;
    const days = (t - f) / 86400000;
    return days > PIVOT_MAX_WEEKS * 7;
  }, [isPivot, queryParams.fromDate, queryParams.toDate]);

  // Synthesize id from employeeId + weekStartDate (unique per long row).
  const data = useMemo(() => rawData.map(r => ({
    ...r,
    id: `${r.employeeId}|${r.weekStartDate}`,
  })), [rawData]);

  // ----- Pivot transformation -----
  const pivotResult = useMemo(() => {
    if (!isPivot) return null;

    // 1. Distinct sorted week keys (YYYY-MM-DD).
    const weekSet = new Set<string>();
    rawData.forEach(r => weekSet.add(r.weekStartDate.split('T')[0]));
    const allWeeks = Array.from(weekSet).sort();

    // 2. Cap to the most recent 8 weeks.
    const clamped = allWeeks.length > PIVOT_MAX_WEEKS;
    const weekColumns = clamped
      ? allWeeks.slice(allWeeks.length - PIVOT_MAX_WEEKS)
      : allWeeks;
    const visibleWeeks = new Set(weekColumns);

    // 3. Bucket by employee. Build per-row totals over the visible weeks only.
    const byEmployee = new Map<string, PivotRow>();
    rawData.forEach(r => {
      const wk = r.weekStartDate.split('T')[0];
      if (!visibleWeeks.has(wk)) return;
      let row = byEmployee.get(r.employeeId);
      if (!row) {
        row = {
          employeeId: r.employeeId,
          employeeCode: r.employeeCode,
          employeeName: r.employeeName,
          weeks: {},
          totalLogged: 0,
          totalMinimum: 0,
          totalShortfall: 0,
        };
        byEmployee.set(r.employeeId, row);
      }
      row.weeks[wk] = {
        statusCode: r.statusCode,
        loggedMinutes: r.totalMinutes || 0,
        minimumMinutes: r.minimumMinutes || 0,
        shortfallMinutes: r.shortfallMinutes || 0,
      };
      row.totalLogged += r.totalMinutes || 0;
      row.totalMinimum += r.minimumMinutes || 0;
      row.totalShortfall += r.shortfallMinutes || 0;
    });

    // 4. Apply the search bar to employee names (pivot rows only).
    const q = searchQuery.trim().toLowerCase();
    let rows = Array.from(byEmployee.values());
    if (q) {
      rows = rows.filter(r =>
        (r.employeeName || '').toLowerCase().includes(q) ||
        (r.employeeCode || '').toLowerCase().includes(q),
      );
    }

    // 5. Sort.
    if (pivotSort === 'shortfall') {
      rows.sort((a, b) => b.totalShortfall - a.totalShortfall);
    } else {
      rows.sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''));
    }

    // 6. Per-week team totals (for the footer row).
    const weekTotals: Record<string, { logged: number; minimum: number; shortfall: number }> = {};
    weekColumns.forEach(wk => {
      const t = { logged: 0, minimum: 0, shortfall: 0 };
      rows.forEach(r => {
        const c = r.weeks[wk];
        if (!c) return;
        t.logged += c.loggedMinutes;
        t.minimum += c.minimumMinutes;
        t.shortfall += c.shortfallMinutes;
      });
      weekTotals[wk] = t;
    });
    const grandTotal = rows.reduce(
      (acc, r) => ({
        logged: acc.logged + r.totalLogged,
        minimum: acc.minimum + r.totalMinimum,
        shortfall: acc.shortfall + r.totalShortfall,
      }),
      { logged: 0, minimum: 0, shortfall: 0 },
    );

    return { rows, weekColumns, weekTotals, grandTotal, clamped, totalWeeksAvailable: allWeeks.length };
  }, [isPivot, rawData, searchQuery, pivotSort]);

  // ----- List-mode handlers -----
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      statusFilter: statusFilter || undefined,
      managerId: managerFilter || undefined,
      allEmployees: !managerFilter,
      page: 1,
    }));
    setFilterOpen(false);
  }, [fromDate, toDate, statusFilter, managerFilter]);

  const clearFilters = useCallback(() => {
    // Reset to default scope: admin-class users get "All", everyone else
    // is anchored back to their own id (matches the initial-load behavior).
    const defaultManagerId = canViewAll ? '' : (currentEmployee?.id || '');
    setFromDate(firstOfMonth());
    setToDate(today());
    setStatusFilter('');
    setManagerFilter(defaultManagerId);
    setQueryParams({
      page: 1,
      pageSize: queryParams.pageSize || 25,
      fromDate: firstOfMonth(),
      toDate: today(),
      managerId: defaultManagerId || undefined,
      allEmployees: !defaultManagerId,
    });
  }, [queryParams.pageSize, canViewAll, currentEmployee?.id]);

  // ----- List-mode summary footer counts -----
  const visibleCounts = useMemo(() => {
    const counts = { notStarted: 0, draft: 0, submitted: 0, total: pagination.totalItems };
    data.forEach(r => {
      if (r.statusCode === 'NOT_STARTED') counts.notStarted++;
      else if (r.statusCode === 'DRAFT') counts.draft++;
      else if (r.statusCode === 'SUBMITTED') counts.submitted++;
    });
    return counts;
  }, [data, pagination.totalItems]);

  type Row = TimesheetStatusReportRow & { id: string };
  const tableColumns: SyutGridColumn<Row>[] = useMemo(() => [
    { id: 'employeeName', header: 'Employee', accessorKey: 'employeeName', size: 200, enableSorting: true },
    { id: 'employeeCode', header: 'Code', accessorKey: 'employeeCode', size: 100 },
    {
      id: 'weekStartDate', header: 'Week Start', accessorKey: 'weekStartDate', size: 120, enableSorting: true,
      cell: ({ row }: any) => formatDate(row.original.weekStartDate),
    },
    {
      id: 'weekEndDate', header: 'Week End', accessorKey: 'weekEndDate', size: 120,
      cell: ({ row }: any) => formatDate(row.original.weekEndDate),
    },
    {
      id: 'statusCode', header: 'Status', accessorKey: 'statusCode', size: 120,
      cell: ({ row }: any) => {
        const code = row.original.statusCode;
        const cls = code === 'SUBMITTED' ? 'badge-active'
                  : code === 'DRAFT' ? 'badge-pending'
                  : 'badge-inactive';
        return <span className={`badge ${cls}`}>{row.original.statusName}</span>;
      },
    },
    {
      id: 'totalHours', header: 'Logged', accessorKey: 'totalMinutes', size: 100,
      cell: ({ row }: any) => formatMinutesToHours(row.original.totalMinutes),
    },
    {
      id: 'minimumHours', header: 'Min Req', accessorKey: 'minimumMinutes', size: 100,
      cell: ({ row }: any) => formatMinutesToHours(row.original.minimumMinutes),
    },
    {
      id: 'shortfallMinutes', header: 'Shortfall', accessorKey: 'shortfallMinutes', size: 110,
      cell: ({ row }: any) => {
        const sf = row.original.shortfallMinutes;
        if (!sf || sf <= 0) return <span style={{ color: '#16a34a' }}>✓</span>;
        return <span style={{ color: '#dc2626', fontWeight: 600 }}>{formatMinutesToHours(sf)}</span>;
      },
    },
    {
      id: 'submittedAt', header: 'Submitted', accessorKey: 'submittedAt', size: 130,
      cell: ({ row }: any) => formatDate(row.original.submittedAt),
    },
  ], []);

  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh',
      onClick: () => refetch(), variant: 'icon' as const,
    },
  ], [refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Timesheet Status Report"
        description="Weekly submission status per employee with shortfall tracking"
        icon={<IconSystem.clock size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search employee...' }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list',  icon: <IconSystem.list size={16} />,  label: 'List',  tooltip: 'List view (one row per employee × week)' },
            { id: 'pivot', icon: <IconSystem.grid size={16} />,  label: 'Pivot', tooltip: 'Pivot view (employees × weeks, max 8 weeks)' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          {/* minWidth: 0 lets this flex child shrink below its content width.
              Without it, the pivot table's `width: max-content` would grow the
              column to fit the table and horizontal scroll on the inner wrapper
              never engages. */}
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Pivot clamp banner — shown when the user's selected range
                covers more than PIVOT_MAX_WEEKS weeks. The API call itself is
                narrowed to the cap (see `effectiveQuery` above), so the
                wording focuses on what the USER chose vs what's displayed. */}
            {isPivot && userRangeExceedsCap && (
              <div style={{
                flexShrink: 0,
                padding: '0.45rem 0.9rem',
                fontSize: '0.78rem',
                color: '#92400e',
                background: '#fef3c7',
                borderTop: '1px solid #f59e0b',
                borderBottom: '1px solid #f59e0b',
              }}>
                <IconSystem.info size={12} /> Pivot view is showing the most recent {PIVOT_MAX_WEEKS} weeks
                of your selected date range. Earlier weeks are hidden — narrow the
                date range or switch to List view to see them.
              </div>
            )}

            {/* ===== List view ===== */}
            {!isPivot && (
              <>
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <SyutGrid
                    data={data}
                    columns={tableColumns}
                    enablePagination
                    manualPagination
                    totalCount={pagination.totalItems}
                    currentPage={(queryParams.page ?? 1) - 1}
                    pageSize={queryParams.pageSize ?? 25}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    enableSorting
                    emptyMessage={loading ? 'Loading...' : 'No weeks in the selected range.'}
                  />
                </div>

                {/* Summary footer (list mode only) */}
                <div style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  padding: '0.4rem 0.9rem',
                  background: 'var(--bg-secondary, #f8fafc)',
                  border: '1px solid var(--border-color)',
                  borderTop: '2px solid var(--border-color)',
                  fontSize: '0.8125rem',
                  flexWrap: 'wrap',
                }}>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {visibleCounts.total} total {visibleCounts.total === 1 ? 'record' : 'records'}
                  </span>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <span><span className="badge badge-inactive">Not Started</span> {visibleCounts.notStarted}</span>
                    <span><span className="badge badge-pending">Draft</span> {visibleCounts.draft}</span>
                    <span><span className="badge badge-active">Submitted</span> {visibleCounts.submitted}</span>
                  </div>
                </div>
              </>
            )}

            {/* ===== Pivot view ===== */}
            {isPivot && (
              <PivotTable
                rows={pivotResult?.rows || []}
                weekColumns={pivotResult?.weekColumns || []}
                weekTotals={pivotResult?.weekTotals || {}}
                grandTotal={pivotResult?.grandTotal || { logged: 0, minimum: 0, shortfall: 0 }}
                sort={pivotSort}
                onSortChange={setPivotSort}
                loading={loading}
              />
            )}
          </div>
        </div>

        {/* Filter panel */}
        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              {/* Pivot-mode cap notice — surfaced in the filter panel so the
                  user sees the rule at the moment they pick a date range. */}
              {isPivot && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.4rem',
                    padding: '0.4rem 0.55rem',
                    marginBottom: '0.5rem',
                    fontSize: '0.72rem',
                    lineHeight: 1.35,
                    color: '#92400e',
                    background: '#fffbeb',
                    border: '1px solid #fcd34d',
                    borderRadius: 4,
                  }}
                >
                  <IconSystem.info size={12} style={{ marginTop: 2, flexShrink: 0 }} />
                  <span>
                    Pivot view is capped at the most recent <strong>{PIVOT_MAX_WEEKS} weeks</strong>.
                    If the date range covers more, earlier weeks are hidden.
                  </span>
                </div>
              )}
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
                <input type="date" className="form-input" value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
                <input type="date" className="form-input" value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              {/* Status filter — hidden in pivot mode (the filter has no
                  meaningful per-employee-row interpretation). The state is
                  preserved so toggling back to List mode keeps the user's
                  prior status filter intact. */}
              {!isPivot && (
                <div className="filter-section">
                  <SyutSelect label="Status" options={STATUS_FILTER_OPTIONS} value={statusFilter}
                    onChange={(val) => setStatusFilter((val as string) || '')} isClearable={false} size="sm" />
                </div>
              )}
              <div className="filter-section">
                <SyutScopedEmployeeSelect
                  label="Manager (reportees)"
                  scopeMode="view"
                  moduleId="timesheet-status-report"
                  role="manager"
                  value={managerFilter || undefined}
                  onChange={(val) => setManagerFilter(val || '')}
                  seedOptions={seedSelfOption}
                  isClearable={canViewAll}
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// PivotTable — inline component
// ============================================================================
// Native HTML table (not SyutGrid) because the grid's column-config doesn't
// pivot well, and we need fine-grained sticky-left + sticky-thead + sticky-tfoot
// behaviour the grid wrapper doesn't expose.

interface PivotTableProps {
  rows: PivotRow[];
  weekColumns: string[];
  weekTotals: Record<string, { logged: number; minimum: number; shortfall: number }>;
  grandTotal: { logged: number; minimum: number; shortfall: number };
  sort: 'name' | 'shortfall';
  onSortChange: (s: 'name' | 'shortfall') => void;
  loading: boolean;
}

const PivotTable: React.FC<PivotTableProps> = ({
  rows, weekColumns, weekTotals, grandTotal, sort, onSortChange, loading,
}) => {
  if (loading && rows.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
        Loading...
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
        No employees in the selected scope and date range.
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', minHeight: 0, position: 'relative' }}>
      <table
        style={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          fontSize: '0.78rem',
          width: 'max-content',
          minWidth: '100%',
          tableLayout: 'fixed',
        }}
      >
        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
          <tr>
            <th
              onClick={() => onSortChange('name')}
              style={{
                ...stickyLeftHeaderStyle,
                cursor: 'pointer',
                width: 220,
              }}
              title="Sort by employee name"
            >
              Employee {sort === 'name' && <IconSystem.chevronDown size={12} />}
            </th>
            {weekColumns.map(wk => (
              <th key={wk} style={weekHeaderStyle}>
                {formatWeekHeader(wk)}
              </th>
            ))}
            <th
              onClick={() => onSortChange('shortfall')}
              style={{ ...weekHeaderStyle, ...totalHeaderStyle, cursor: 'pointer' }}
              title="Sort by total shortfall (descending)"
            >
              Total {sort === 'shortfall' && <IconSystem.chevronDown size={12} />}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.employeeId}>
              <td style={stickyLeftCellStyle}>
                <div style={{ fontWeight: 600 }}>{r.employeeName || '—'}</div>
                {r.employeeCode && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{r.employeeCode}</div>
                )}
              </td>
              {weekColumns.map(wk => (
                <td key={wk} style={cellShellStyle}>
                  <PivotCellView cell={r.weeks[wk]} />
                </td>
              ))}
              <td style={{ ...cellShellStyle, ...totalCellStyle }}>
                <StackedNumbers
                  logged={r.totalLogged}
                  minimum={r.totalMinimum}
                  shortfall={r.totalShortfall}
                  bold
                />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 2 }}>
          <tr>
            <td style={{ ...stickyLeftFooterStyle, fontWeight: 600 }}>Team Total</td>
            {weekColumns.map(wk => {
              const t = weekTotals[wk] || { logged: 0, minimum: 0, shortfall: 0 };
              return (
                <td key={wk} style={footerCellShellStyle}>
                  <StackedNumbers logged={t.logged} minimum={t.minimum} shortfall={t.shortfall} bold />
                </td>
              );
            })}
            <td style={{ ...footerCellShellStyle, ...totalCellStyle }}>
              <StackedNumbers
                logged={grandTotal.logged}
                minimum={grandTotal.minimum}
                shortfall={grandTotal.shortfall}
                bold
              />
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cell renderer — three stacked lines (Logged / Min / Shortfall).
//   shortfall === 0          → light green
//   shortfall > 0            → red
//   statusCode = NOT_STARTED → grey + dashed border
//   statusCode = DRAFT       → keep underlying severity colour, add a subtle
//                              yellow left border so it's visually distinct
//                              from SUBMITTED.
// ---------------------------------------------------------------------------

const PivotCellView: React.FC<{ cell?: PivotCell }> = ({ cell }) => {
  if (!cell) {
    return (
      <div style={emptyCellStyle}>
        <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>—</span>
      </div>
    );
  }

  if (cell.statusCode === 'NOT_STARTED') {
    return (
      <div style={notStartedCellStyle}>
        <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>Not Started</div>
        <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
          Min {formatMinutesToHours(cell.minimumMinutes)}
        </div>
        {cell.shortfallMinutes > 0 && (
          <div style={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 600 }}>
            Short {formatMinutesToHours(cell.shortfallMinutes)}
          </div>
        )}
      </div>
    );
  }

  const isOk = cell.shortfallMinutes <= 0;
  const isDraft = cell.statusCode === 'DRAFT';
  const style: React.CSSProperties = {
    ...filledCellStyle,
    background: isOk ? '#dcfce7' : '#fee2e2',
    color: isOk ? '#166534' : '#991b1b',
    borderLeft: isDraft ? '3px solid #f59e0b' : '3px solid transparent',
  };

  return (
    <div style={style}>
      <StackedNumbers
        logged={cell.loggedMinutes}
        minimum={cell.minimumMinutes}
        shortfall={cell.shortfallMinutes}
      />
    </div>
  );
};

const StackedNumbers: React.FC<{
  logged: number;
  minimum: number;
  shortfall: number;
  bold?: boolean;
}> = ({ logged, minimum, shortfall, bold }) => (
  <>
    <div style={{ fontWeight: bold ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}>
      {formatMinutesToHours(logged)}
    </div>
    <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', fontVariantNumeric: 'tabular-nums' }}>
      Min {formatMinutesToHours(minimum)}
    </div>
    {shortfall > 0 && (
      <div style={{ color: '#dc2626', fontWeight: 600, fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums' }}>
        Short {formatMinutesToHours(shortfall)}
      </div>
    )}
  </>
);

// ---------------------------------------------------------------------------
// Inline style objects — kept here so the table is self-contained.
// ---------------------------------------------------------------------------

const baseCellPadding = '0.35rem 0.5rem';

const weekHeaderStyle: React.CSSProperties = {
  background: 'var(--grid-header-bg, #dbeafe)',
  borderBottom: '2px solid var(--border-color)',
  borderRight: '1px solid var(--border-color)',
  padding: baseCellPadding,
  textAlign: 'center',
  fontSize: '0.78rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  width: 110,
  height: 46,
};

const totalHeaderStyle: React.CSSProperties = {
  borderLeft: '2px solid var(--border-color)',
  background: 'var(--filter-header-bg, #c7d8ef)',
};

const stickyLeftHeaderStyle: React.CSSProperties = {
  ...weekHeaderStyle,
  position: 'sticky',
  left: 0,
  zIndex: 4,
  textAlign: 'left',
  background: 'var(--filter-header-bg, #c7d8ef)',
};

const cellShellStyle: React.CSSProperties = {
  borderRight: '1px solid var(--border-color)',
  borderBottom: '1px solid var(--border-color)',
  padding: '0.2rem 0.25rem',
  verticalAlign: 'middle',
  width: 110,
};

const stickyLeftCellStyle: React.CSSProperties = {
  ...cellShellStyle,
  position: 'sticky',
  left: 0,
  zIndex: 1,
  background: 'var(--bg-primary, #ffffff)',
  width: 220,
  padding: baseCellPadding,
};

const totalCellStyle: React.CSSProperties = {
  borderLeft: '2px solid var(--border-color)',
  background: 'var(--bg-secondary, #f8fafc)',
};

const filledCellStyle: React.CSSProperties = {
  padding: '0.25rem 0.4rem',
  borderRadius: '3px',
  margin: '0.1rem',
  textAlign: 'center',
};

const notStartedCellStyle: React.CSSProperties = {
  padding: '0.25rem 0.4rem',
  borderRadius: '3px',
  margin: '0.1rem',
  textAlign: 'center',
  background: '#f3f4f6',
  border: '1px dashed #9ca3af',
};

const emptyCellStyle: React.CSSProperties = {
  padding: '0.25rem',
  textAlign: 'center',
  color: 'var(--text-muted)',
};

const footerCellShellStyle: React.CSSProperties = {
  ...cellShellStyle,
  background: 'var(--bg-secondary, #f8fafc)',
  borderTop: '2px solid var(--border-color)',
  fontWeight: 600,
};

const stickyLeftFooterStyle: React.CSSProperties = {
  ...footerCellShellStyle,
  position: 'sticky',
  left: 0,
  zIndex: 3,
  width: 220,
  padding: baseCellPadding,
  textAlign: 'left',
};

export default TimesheetStatusReportScreen;
